import { stableHash } from '@/shared/utils/stableHash';

import { type BuildQuizListItem } from '../form/schema/BuildQuizListSchema';

/**
 * 성취기준(curriculumList) 유효성 판정용 콘텐츠 지문 유틸.
 *
 * 성취기준은 "그 시점의 문항 내용"에 대해 매겨진 값이다. 이후 사용자가 문항을
 * 편집하면 매핑이 유효하다고 보장할 수 없으므로, 성취기준이 폼에 들어오는 시점
 * (AI 생성 유입 / 편집 모드 복원)의 내용 지문을 `curriculumFingerprint` 로 함께
 * 저장해 두고, 내보내는 시점(submit / AI 세션 스냅샷 직렬화)에 현재 내용 지문과
 * 비교해 다르면 성취기준을 버린다.
 *
 * 편집 이벤트 추적 대신 내용 비교를 쓰는 이유: 문항 편집이 controlled onChange
 * 외에 programmatic setValue(AI 보기/답안 생성 등) 경로로도 일어나 이벤트 기반
 * 감지가 불완전하고, 내용이 같다면 어떤 경로로 다시 써져도 편집으로 오인하면
 * 안 되기 때문(목록 재배치·복사·스냅샷 왕복 등).
 */

/** 문항 유형별 정답 영역의 내용 투영. key(nanoid) 등 비내용 필드는 제외한다. */
const projectQuizAnswerContent = (quiz: BuildQuizListItem) => {
  switch (quiz.type) {
    case 'multiple':
      return quiz.multipleData.choiceList.map(choice => ({
        text: choice.text ?? '',
        richText: choice.richText ?? '',
        imageUrl: choice.imageUrl ?? '',
        isAnswer: choice.isAnswer,
      }));
    case 'subjective':
      return quiz.subjectiveData.answerList.map(answer => answer.text);
    case 'ox':
      return quiz.oxData.answer;
    case 'match':
      return {
        leftItems: quiz.matchData.leftItems,
        rightItems: quiz.matchData.rightItems,
        answerPairs: quiz.matchData.answerPairs,
      };
    case 'order':
      return quiz.orderData?.items.map(item => ({
        text: item.text,
        richText: item.richText ?? '',
        imageUrl: item.imageUrl ?? '',
        equation: item.equation ?? '',
      }));
    case 'essay':
      return null;
  }
};

/** 사용자가 편집 가능한 문항 내용만으로 만든 결정적 지문. id/origin 등은 제외. */
export const createCurriculumFingerprint = (quiz: BuildQuizListItem): string =>
  stableHash({
    type: quiz.type,
    question: quiz.question ?? '',
    questionRichText: quiz.questionRichText ?? '',
    questionImageUrl: quiz.questionImageUrl ?? '',
    hint: quiz.hint ?? null,
    answerContent: projectQuizAnswerContent(quiz),
  });

/**
 * 성취기준이 있는 문항에 현재 내용 지문을 찍는다. 성취기준이 폼에 유입되는
 * 지점(AI 생성 어댑터, 편집 모드 복원 어댑터)에서 호출해야 한다.
 */
export const withCurriculumFingerprint = <T extends BuildQuizListItem>(
  quiz: T,
): T => {
  if (!quiz.curriculumList?.length) return quiz;

  return {
    ...quiz,
    curriculumFingerprint: createCurriculumFingerprint(quiz),
  };
};

/**
 * 제출/직렬화에 실어도 되는 성취기준을 반환한다.
 * 지문이 없거나(비정상 유입) 현재 내용과 어긋나면(=부여 이후 편집됨) undefined.
 */
export const getEffectiveCurriculumList = (
  quiz: BuildQuizListItem,
): BuildQuizListItem['curriculumList'] => {
  if (!quiz.curriculumList?.length) return quiz.curriculumList;
  if (quiz.curriculumFingerprint !== createCurriculumFingerprint(quiz)) {
    return undefined;
  }

  return quiz.curriculumList;
};
